import React from 'react'
//import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    AspectRatio,
    Box,
    HStack,
    // IconButton,
    useMultiStyleConfig,
    VisuallyHidden,
    Button,
    Flex,
    Text,
} from '@chakra-ui/react'
import Link from '@salesforce/retail-react-app/app/components/link'

import {CompareIcon} from '../../icons'
import {InformativeLabelGroup} from '../../informative-label'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getImageForProductTile} from '../../../utils/utils'
import DynamicImage from '../../dynamic-image'
import {remCalc} from '../../../theme/_utils'
import CheckIconCircle from '../../icons/shared/others/CheckIconCircle'

const ProductTileImage = ({
    dynamicImageProps = {
        widths: ['50vw', '50vw', '20vw', '20vw', '25vw'],
    },
    product,
    productUrl,
    compactView,
    priceData,
    variant,
    customImage,
    selectItemTileEventGtm = null,
    isOfficina = false,
    isCorporateSale = false,
    corporateSaleLabelName = '',
    isDragging = false,
    height = 'auto',
    imageStyle = {},
    isInformativeLabelActive = true,
    bundleFirstElement = false,
    isFetchPriority = false,
    showOutOfStock = false,
    isFreeGift = false,
    onCompareSelect = null,
    compareDisabled = false,
    isHovered = false,
    isCompareModal = false,
    isCompareActive = false,
    viewType = 'small',
}) => {
    const styles = useMultiStyleConfig('ProductTile', {
        variant: variant || (compactView ? 'compact' : 'default'),
    })

    const localizedProductName =
        product?.c_variantIdentifier || product?.name || product?.productName
    const image = getImageForProductTile(product, customImage, viewType)

    const productImageAltText = [
        product?.c_categoryName || product?.c_defaultCategoryName,
        product?.productName || product?.c_defaultName,
        product?.representedProduct?.id,
    ]
        .filter(Boolean)
        .join(', ')

    const hoverImageProps = {
        transform: isHovered ? 'scale(1.05)' : 'scale(1)',
        transition: 'transform 0.3s ease',
    }
    const intl = useIntl()
    const compareText = intl
        .formatMessage({
            id: 'product_tile.compare.label',
            defaultMessage: 'Compare',
        })
        .toUpperCase()
    const selectedText = intl
        .formatMessage({
            id: 'product_tile.selected.label',
            defaultMessage: 'Selected',
        })
        .toUpperCase()

    return (
        <Box
            {...styles.imageWrapper}
            data-testid="product-tile-image-wrapper"
            sx={
                imageStyle && Object.keys(imageStyle).length > 0 ? {'& img': imageStyle} : undefined
            }
        >
            <VisuallyHidden>
                {/* <Box role="status" aria-live="assertive" aria-atomic="true">
                    {alertMessage}
                </Box> */}
            </VisuallyHidden>
            <AspectRatio
                {...styles.image}
                {...(bundleFirstElement ? styles.firstBundleImage : {})}
                {...(variant === 'bundle' ? {...styles.braunBundleBorderRadius} : {})}
            >
                {productUrl && !isDragging ? (
                    <ProductImageLink
                        productUrl={productUrl}
                        isOfficina={isOfficina}
                        selectItemTileEventGtm={selectItemTileEventGtm}
                        productName={localizedProductName}
                    >
                        <>
                            <DynamicImage
                                data-testid="product-tile-image"
                                src={`${image}[?sw={width}&q=60]`}
                                imageProps={{
                                    height: height,
                                    alt: productImageAltText,
                                    loading: 'lazy',
                                    ...dynamicImageProps?.imageProps,
                                    ...hoverImageProps,
                                    ...imageStyle,
                                }}
                                isFetchPriority={isFetchPriority}
                                {...imageStyle}
                            />
                        </>
                    </ProductImageLink>
                ) : (
                    <>
                        {image && (
                            <DynamicImage
                                data-testid="product-tile-image"
                                src={`${image}[?sw={width}&q=600]`}
                                // widths={dynamicImageProps?.widths} // TODO: readd this prop when we have a solution for dynamic image widths
                                imageProps={{
                                    height: height,
                                    alt: '',
                                    loading: 'lazy',
                                    ...dynamicImageProps?.imageProps,
                                    ...hoverImageProps,
                                    ...imageStyle,
                                }}
                                isFetchPriority={isFetchPriority}
                                {...imageStyle}
                            />
                        )}
                        <Box {...styles.imageOverlay} />
                    </>
                )}
            </AspectRatio>
            <HStack {...styles.badgeGroup}>
                <Flex {...styles?.imageLabelsContainer}>
                    {isInformativeLabelActive && (
                        <InformativeLabelGroup
                            product={product?.representedProduct ?? product}
                            priceData={priceData}
                            isFreeGift={isFreeGift}
                            isCorporateSale={isCorporateSale}
                            corporateSaleLabelName={corporateSaleLabelName}
                            showOutOfStock={showOutOfStock}
                        />
                    )}
                </Flex>
                {!isCompareModal && onCompareSelect && (
                    <Flex maxH={remCalc(24)}>
                        <Button
                            variant="icon-button-outline"
                            size="small"
                            isDisabled={compareDisabled}
                            {...(isCompareActive && styles.compareButtonActive)}
                            leftIcon={
                                isCompareActive ? (
                                    <CheckIconCircle
                                        width={remCalc(16)}
                                        height={remCalc(16)}
                                        color="foreground.neutral.inverse"
                                    />
                                ) : (
                                    <CompareIcon
                                        width={remCalc(16)}
                                        height={remCalc(16)}
                                        color="foreground.neutral.default"
                                    />
                                )
                            }
                            onClick={() => onCompareSelect(product)}
                        >
                            <Text
                                textStyle="body-xxsmall"
                                fontWeight="bold"
                                color={
                                    isCompareActive
                                        ? 'foreground.neutral.inverse'
                                        : 'foreground.neutral.default'
                                }
                            >
                                {isCompareActive ? selectedText : compareText}
                            </Text>
                        </Button>
                    </Flex>
                )}
            </HStack>
        </Box>
    )
}

ProductTileImage.propTypes = {
    dynamicImageProps: PropTypes.object,
    enableFavourite: PropTypes.bool,
    isFavourite: PropTypes.bool,
    imageViewType: PropTypes.string,
    product: PropTypes.object,
    productUrl: PropTypes.string,
    selectableAttributeId: PropTypes.string,
    compactView: PropTypes.bool,
    priceData: PropTypes.object,
    variant: PropTypes.string,
    customImage: PropTypes.string,
    isOfficina: PropTypes.bool,
    isCorporateSale: PropTypes.bool,
    corporateSaleLabelName: PropTypes.string,
    selectItemTileEventGtm: PropTypes.func,
    isDragging: PropTypes.bool,
    height: PropTypes.string,
    imageStyle: PropTypes.object,
    isInformativeLabelActive: PropTypes.bool,
    bundleFirstElement: PropTypes.bool,
    isFetchPriority: PropTypes.bool,
    showOutOfStock: PropTypes.bool,
    isFreeGift: PropTypes.bool,
    onCompareSelect: PropTypes.func,
    compareDisabled: PropTypes.bool,
    isHovered: PropTypes.bool,
    isCompareActive: PropTypes.bool,
    isCompareModal: PropTypes.bool,
    viewType: PropTypes.string,
}

export default ProductTileImage

const ProductImageLink = ({
    productUrl,
    isOfficina,
    selectItemTileEventGtm,
    productName,
    children,
}) => {
    const {locale} = useMultiSite()
    const intl = useIntl()
    const localeId = locale?.alias

    const ariaLabel = intl.formatMessage(
        {
            id: 'product_tile.view_product.aria_label',
            defaultMessage: 'View {productName}',
        },
        {
            productName,
        },
    )
    return isOfficina ? (
        <a
            href={`/${localeId}${productUrl}`}
            aria-label={ariaLabel}
            onClick={selectItemTileEventGtm}
        >
            {children}
        </a>
    ) : (
        <Link to={productUrl} aria-label={ariaLabel} onClick={selectItemTileEventGtm}>
            {children}
        </Link>
    )
}

ProductImageLink.propTypes = {
    productUrl: PropTypes.string,
    children: PropTypes.node,
    isOfficina: PropTypes.bool,
    selectItemTileEventGtm: PropTypes.func,
    productName: PropTypes.string,
}
